'use client';

import {formatDate, formatDateTime} from '@sollapay/utils';

import {TableColumnHeader} from './TableColumnHeader';
import {cn} from '../../lib/utils';
import {Button} from '../shadcn/button';
import {Typography} from '../Typography/Typography';

import type {
  ButtonColumnOptions,
  DateColumnOptions,
  NodeColumnOptions,
  TextColumnOptions
} from './types';
import type {ColumnDef} from '@tanstack/react-table';

export function textColumn<TData>(options: TextColumnOptions): ColumnDef<TData, string> {
  const {accessorKey, header, enableSorting = true, className, width, minWidth, maxWidth} = options;
  return {
    accessorKey,
    enableSorting,
    meta: {width, minWidth, maxWidth},
    header: ({column}) =>
      enableSorting ? <TableColumnHeader column={column} title={header} /> : <span>{header}</span>,
    cell: ({getValue}) => (
      <Typography size="sm" className={className}>
        {getValue()}
      </Typography>
    )
  };
}

export function dateColumn<TData>(options: DateColumnOptions): ColumnDef<TData, string> {
  const {
    accessorKey,
    header,
    format = 'date',
    enableSorting = true,
    className,
    width,
    minWidth,
    maxWidth
  } = options;
  const formatter = format === 'datetime' ? formatDateTime : formatDate;
  return {
    accessorKey,
    enableSorting,
    meta: {width, minWidth, maxWidth},
    header: ({column}) =>
      enableSorting ? <TableColumnHeader column={column} title={header} /> : <span>{header}</span>,
    cell: ({getValue}) => {
      const value = getValue();
      if (!value) return null;
      return (
        <Typography size="sm" color="secondary" className={cn('tabular-nums', className)}>
          {formatter(value)}
        </Typography>
      );
    }
  };
}

export function buttonColumn<TData>(options: ButtonColumnOptions<TData>): ColumnDef<TData, never> {
  const {
    id,
    label,
    icon,
    iconPosition,
    variant = 'secondary',
    size = 'sm',
    destructive,
    onClick,
    disabled,
    width,
    minWidth,
    maxWidth
  } = options;
  return {
    id,
    enableSorting: false,
    meta: {width, minWidth, maxWidth},
    header: () => null,
    cell: ({row}) => {
      const resolvedLabel = typeof label === 'function' ? label(row.original) : label;
      const resolvedDisabled =
        typeof disabled === 'function' ? disabled(row.original) : (disabled ?? false);
      return (
        <Button
          variant={variant}
          size={size}
          destructive={destructive}
          disabled={resolvedDisabled}
          icon={icon}
          iconPosition={iconPosition}
          onClick={() => onClick(row.original)}
        >
          {resolvedLabel}
        </Button>
      );
    }
  };
}

export function nodeColumn<TData>(options: NodeColumnOptions<TData>): ColumnDef<TData, unknown> {
  const {
    id,
    header,
    cell,
    accessorFn,
    enableSorting = true,
    className,
    width,
    minWidth,
    maxWidth
  } = options;
  return {
    id,
    ...(accessorFn ? {accessorFn} : {}),
    enableSorting,
    meta: {width, minWidth, maxWidth},
    header: ({column}) =>
      enableSorting ? <TableColumnHeader column={column} title={header} /> : <span>{header}</span>,
    cell: ({row}) => <div className={cn(className)}>{cell(row.original)}</div>
  };
}
